import React, { useEffect, useRef, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { userService } from '@/shared/services/userService';
import {
    formatFileSize,
    formatLongDate,
} from '@/features/applications/student/programs/shared';
import { ResumeDownload } from '@/features/applications/student/programs/ResumeAnswer';

const HELP = 'PDF, DOC or DOCX, up to 5 MB.';
const UPLOAD_FAILED = 'The resume could not be uploaded. Try again.';
const REMOVE_FAILED = 'The resume could not be removed. Try again.';

/**
 * The student's profile resume: choose a file, replace it, download it, remove
 * it. Every application uses this one resume and keeps its own copy when it is
 * submitted, so a change here never alters an application already sent.
 * `resume` is { fileName, size, uploadedAt } or null; onChange gets the new
 * value after the server accepts a change. With `allowChange` off only the
 * file's details and download show.
 */
function ResumeUpload({
    id,
    resume,
    onChange,
    label,
    required = false,
    invalid = false,
    allowChange = true,
}) {
    const inputRef = useRef(null);
    const replaceRef = useRef(null);
    const pendingFocus = useRef(null);
    const mounted = useRef(true);
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState('');
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    // The control the person just used may be gone after the change, so focus
    // goes to its replacement rather than to the page.
    useEffect(() => {
        if (pendingFocus.current === 'replace') replaceRef.current?.focus();
        if (pendingFocus.current === 'input') inputRef.current?.focus();
        pendingFocus.current = null;
    }, [resume]);

    const say = (text, isError = false) => {
        if (!mounted.current) return;
        setMessage(text);
        setFailed(isError);
    };

    const handleChoose = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file || busy) return;
        setBusy(true);
        say(`Uploading ${file.name}`);
        try {
            const { resume: saved } = await userService.uploadResume(file);
            pendingFocus.current = 'replace';
            onChange(saved);
            say(`${saved.fileName} saved to your profile.`);
        } catch (error) {
            say(error.response?.data?.message || UPLOAD_FAILED, true);
        } finally {
            if (mounted.current) setBusy(false);
        }
    };

    const handleRemove = async () => {
        if (busy) return;
        setBusy(true);
        say(`Removing ${resume.fileName}`);
        try {
            await userService.removeResume();
            pendingFocus.current = 'input';
            onChange(null);
            say(`${resume.fileName} removed from your profile.`);
        } catch (error) {
            say(error.response?.data?.message || REMOVE_FAILED, true);
        } finally {
            if (mounted.current) setBusy(false);
        }
    };

    const inputId = `${id}-file`;
    const helpId = `${id}-help`;
    const statusId = `${id}-status`;
    const inputLabel =
        label || (required ? 'Add your resume' : 'Add your resume (optional)');

    return (
        <div className="mb-3">
            <Form.Label htmlFor={inputId} className={resume ? 'd-none' : ''}>
                {inputLabel}
                {required && (
                    <>
                        {' '}
                        <span className="text-danger" aria-hidden="true">
                            *
                        </span>
                        <span className="visually-hidden"> (required)</span>
                    </>
                )}
            </Form.Label>
            <Form.Control
                ref={inputRef}
                id={inputId}
                type="file"
                className={resume ? 'd-none' : undefined}
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                aria-required={required ? 'true' : undefined}
                aria-invalid={invalid ? 'true' : undefined}
                aria-describedby={`${helpId} ${statusId}`}
                aria-disabled={busy ? 'true' : undefined}
                onChange={handleChoose}
            />
            <Form.Text
                id={helpId}
                className={`text-muted d-block${resume ? ' d-none' : ''}`}
            >
                {HELP}
            </Form.Text>
            {resume && (
                <div>
                    <div className="text-break">
                        <strong>{resume.fileName}</strong> (
                        {formatFileSize(resume.size)}), added{' '}
                        {formatLongDate(resume.uploadedAt)}
                    </div>
                    <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
                        <ResumeDownload
                            fetchResume={() => userService.downloadResume()}
                            resume={resume}
                        />
                        {allowChange && (
                            <span className="d-inline-flex flex-nowrap gap-2">
                                <Button
                                    ref={replaceRef}
                                    variant="outline-primary"
                                    size="sm"
                                    aria-label={`Replace resume ${resume.fileName}`}
                                    aria-disabled={busy ? 'true' : undefined}
                                    onClick={() =>
                                        !busy && inputRef.current?.click()
                                    }
                                >
                                    Replace
                                </Button>
                                <Button
                                    variant="outline-danger"
                                    size="sm"
                                    aria-label={`Remove resume ${resume.fileName}`}
                                    aria-disabled={busy ? 'true' : undefined}
                                    onClick={handleRemove}
                                >
                                    Remove
                                </Button>
                            </span>
                        )}
                    </div>
                </div>
            )}
            <div
                id={statusId}
                role="status"
                className={`small mt-1${failed ? ' text-danger' : ''}`}
            >
                {message}
            </div>
        </div>
    );
}

export default ResumeUpload;
